import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useVocabularyTrainer } from '../hooks/useVocabularyTrainer';
import { useAuth } from '../hooks/useAuth';
import type { VocabularyWord } from '@homeapp/shared';

type Tab = 'practice' | 'manage';
type Phase = 'ready' | 'exercise' | 'feedback';

export default function VocabularyTrainerPage() {
  const { user } = useAuth();
  const {
    exercise,
    progressOverview,
    words,
    loading,
    exerciseLoading,
    error,
    lastResult,
    fetchWords,
    fetchProgress,
    nextExercise,
    submitAnswer,
    createWord,
    deleteWord,
    generateBatch,
    setError,
  } = useVocabularyTrainer();

  const [tab, setTab] = useState<Tab>('practice');
  const [phase, setPhase] = useState<Phase>('ready');
  const [selectedText, setSelectedText] = useState('');
  const [showInfo, setShowInfo] = useState(false);
  const [sessionStats, setSessionStats] = useState({ correct: 0, incorrect: 0, total: 0 });
  const [message, setMessage] = useState('');

  const [newWord, setNewWord] = useState<Partial<VocabularyWord>>({
    word: '',
    topic: 'general',
    level: 2,
    partOfSpeech: 'noun',
    englishDefinition: '',
    dutchDescription: '',
    exampleEnglish: '',
    difficulty: 2,
  });

  const [batchConfig, setBatchConfig] = useState({
    topic: 'daily life',
    level: 2,
    count: 50,
    partOfSpeech: 'mixed',
    seedWordsText: '',
  });
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  useEffect(() => {
    if (tab === 'manage') fetchWords();
  }, [tab, fetchWords]);

  if (!user) return <Navigate to="/login" replace />;
  if (!user.access.includes('vocabulary-trainer') && user.role !== 'admin') return <Navigate to="/" replace />;

  const startPractice = async () => {
    setSessionStats({ correct: 0, incorrect: 0, total: 0 });
    setShowInfo(false);
    setSelectedText('');
    await nextExercise();
    setPhase('exercise');
  };

  const handleSubmit = async () => {
    if (!exercise || !selectedText) return;
    const result = await submitAnswer(exercise.wordId, selectedText);
    if (!result) return;

    setSessionStats(prev => ({
      correct: prev.correct + (result.correct ? 1 : 0),
      incorrect: prev.incorrect + (result.correct ? 0 : 1),
      total: prev.total + 1,
    }));
    setPhase('feedback');
  };

  const handleNext = async () => {
    setSelectedText('');
    setShowInfo(false);
    await nextExercise();
    setPhase('exercise');
  };

  const handleStop = () => {
    setPhase('ready');
    setShowInfo(false);
    fetchProgress();
  };

  const handleCreateWord = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await createWord(newWord);
    if (!result) return;

    setMessage(`Created word: ${result.word}`);
    setNewWord({
      word: '',
      topic: newWord.topic || 'general',
      level: newWord.level || 2,
      partOfSpeech: newWord.partOfSpeech || 'noun',
      englishDefinition: '',
      dutchDescription: '',
      exampleEnglish: '',
      difficulty: newWord.difficulty || 2,
    });
    fetchWords();
  };

  const handleGenerateBatch = async () => {
    setGenerating(true);
    setMessage('Generating vocabulary batch...');

    const seedWords = batchConfig.seedWordsText
      .split(/\r?\n|,/)
      .map(v => v.trim())
      .filter(Boolean);

    const result = await generateBatch({
      topic: batchConfig.topic,
      level: batchConfig.level,
      count: batchConfig.count,
      partOfSpeech: batchConfig.partOfSpeech,
      seedWords,
    });

    if (result) {
      setMessage(
        `Generated ${result.createdCount} words (skipped existing: ${result.skippedExisting}, invalid: ${result.skippedInvalid})`,
      );
      fetchWords();
      fetchProgress();
    }
    setGenerating(false);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900">🧠 Vocabulary Trainer</h1>
        <p className="text-gray-600 text-sm mt-1">Smart English vocabulary practice with Dutch + English info</p>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700">✕</button>
        </div>
      )}

      {message && (
        <div className="bg-green-50 text-green-700 p-3 rounded-lg text-sm">
          {message}
          <button onClick={() => setMessage('')} className="ml-2 text-green-500 hover:text-green-700">✕</button>
        </div>
      )}

      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        <TabButton active={tab === 'practice'} onClick={() => setTab('practice')}>🎯 Practice</TabButton>
        <TabButton active={tab === 'manage'} onClick={() => setTab('manage')}>📚 Manage</TabButton>
      </div>

      {tab === 'practice' && (
        <>
          {phase === 'ready' && (
            <div className="space-y-5">
              {progressOverview && (
                <div className="bg-gradient-to-br from-rose-50 to-orange-50 rounded-2xl p-5">
                  <h3 className="font-semibold text-gray-800 mb-3">Your Progress</h3>
                  <div className="grid grid-cols-2 gap-3 text-center">
                    <StatCard label="Words" value={`${progressOverview.practiced}/${progressOverview.totalWords}`} />
                    <StatCard label="Accuracy" value={`${progressOverview.accuracy}%`} />
                    <StatCard label="Correct" value={String(progressOverview.totalCorrect)} color="text-green-600" />
                    <StatCard label="Incorrect" value={String(progressOverview.totalIncorrect)} color="text-red-600" />
                  </div>
                </div>
              )}

              <button
                onClick={startPractice}
                disabled={exerciseLoading || (progressOverview?.totalWords || 0) === 0}
                className="w-full bg-rose-600 text-white py-4 rounded-2xl text-lg font-semibold hover:bg-rose-700 disabled:opacity-50"
              >
                {exerciseLoading ? 'Loading...' : 'Start Smart Training'}
              </button>

              {(progressOverview?.totalWords || 0) === 0 && (
                <p className="text-center text-sm text-gray-500">No words yet. Go to Manage and add words first.</p>
              )}
            </div>
          )}

          {phase === 'exercise' && exercise && (
            <div className="space-y-4">
              <div className="text-center text-sm text-gray-500">
                Exercise #{sessionStats.total + 1} · ✓ {sessionStats.correct} · ✗ {sessionStats.incorrect}
              </div>

              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 space-y-4">
                <div className="text-xs text-gray-500 uppercase tracking-wide">
                  {exercise.topic} · level {exercise.level} · {exercise.partOfSpeech}
                </div>
                <div className="text-3xl font-bold text-gray-900">{exercise.word}</div>
                <p className="text-sm text-gray-600">Pick the best English meaning:</p>

                <div className="space-y-2">
                  {exercise.options.map(option => (
                    <button
                      key={option.optionId}
                      onClick={() => setSelectedText(option.text)}
                      className={`w-full text-left px-3 py-2 rounded-lg border transition ${
                        selectedText === option.text
                          ? 'border-rose-500 bg-rose-50 text-rose-700'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      {option.text}
                    </button>
                  ))}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleSubmit}
                    disabled={!selectedText || exerciseLoading}
                    className="flex-1 bg-rose-600 text-white py-3 rounded-xl font-medium hover:bg-rose-700 disabled:opacity-50"
                  >
                    Check ✓
                  </button>
                  <button
                    onClick={() => setShowInfo(v => !v)}
                    className="bg-gray-200 text-gray-700 py-3 px-4 rounded-xl hover:bg-gray-300"
                  >
                    ℹ Info
                  </button>
                </div>

                {showInfo && (
                  <div className="bg-blue-50 rounded-xl p-3 text-sm space-y-2">
                    <div>
                      <div className="text-blue-700 font-medium">🇳🇱 Nederlands</div>
                      <p className="text-gray-700">{exercise.info.dutchDescription}</p>
                    </div>
                    <div>
                      <div className="text-blue-700 font-medium">🇬🇧 English</div>
                      <p className="text-gray-700">{exercise.info.englishDefinition}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="text-center">
                <button onClick={handleStop} className="text-sm text-gray-500 hover:text-gray-700 underline">Stop practicing</button>
              </div>
            </div>
          )}

          {phase === 'feedback' && lastResult && (
            <div className="space-y-4">
              <div className={`rounded-2xl p-5 ${lastResult.correct ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className="text-center">
                  <div className="text-4xl mb-2">{lastResult.correct ? '✅' : '❌'}</div>
                  <div className={`text-xl font-bold ${lastResult.correct ? 'text-green-800' : 'text-red-800'}`}>
                    {lastResult.correct ? 'Correct!' : 'Incorrect'}
                  </div>
                  {!lastResult.correct && (
                    <p className="text-gray-800 mt-2">
                      Correct answer: <span className="font-semibold">{lastResult.correctAnswer}</span>
                    </p>
                  )}
                </div>

                <div className="mt-4 bg-white/70 rounded-xl p-3 text-sm space-y-2">
                  <div>
                    <div className="text-blue-700 font-medium">🇳🇱 Nederlands</div>
                    <p className="text-gray-700">{lastResult.info.dutchDescription}</p>
                  </div>
                  <div>
                    <div className="text-blue-700 font-medium">🇬🇧 English</div>
                    <p className="text-gray-700">{lastResult.info.englishDefinition}</p>
                  </div>
                </div>
              </div>

              <div className="text-center text-sm text-gray-600">
                Session: ✓ {sessionStats.correct} · ✗ {sessionStats.incorrect} · Total: {sessionStats.total}
              </div>

              <div className="flex gap-3">
                <button onClick={handleNext} className="flex-1 bg-rose-600 text-white py-3 rounded-xl font-medium hover:bg-rose-700">
                  Next →
                </button>
                <button onClick={handleStop} className="bg-gray-200 text-gray-700 py-3 px-4 rounded-xl hover:bg-gray-300">
                  Stop
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'manage' && (
        <div className="space-y-6">
          <section className="bg-white border border-gray-100 rounded-2xl p-4 space-y-3">
            <h2 className="font-semibold text-gray-900">⚡ AI Batch Generator (up to 50)</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                value={batchConfig.topic}
                onChange={e => setBatchConfig(prev => ({ ...prev, topic: e.target.value }))}
                className="px-3 py-2 rounded-lg border border-gray-300"
                placeholder="Topic (e.g. travel)"
              />
              <input
                value={batchConfig.partOfSpeech}
                onChange={e => setBatchConfig(prev => ({ ...prev, partOfSpeech: e.target.value }))}
                className="px-3 py-2 rounded-lg border border-gray-300"
                placeholder="Part of speech (mixed/noun/verb...)"
              />
              <label className="flex items-center gap-2 text-sm text-gray-700">
                Level
                <input
                  type="number"
                  min={1}
                  max={5}
                  value={batchConfig.level}
                  onChange={e => setBatchConfig(prev => ({ ...prev, level: Number(e.target.value) || 2 }))}
                  className="w-20 px-3 py-2 rounded-lg border border-gray-300"
                />
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                Count
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={batchConfig.count}
                  onChange={e => setBatchConfig(prev => ({ ...prev, count: Math.min(50, Math.max(1, Number(e.target.value) || 50)) }))}
                  className="w-20 px-3 py-2 rounded-lg border border-gray-300"
                />
              </label>
            </div>
            <textarea
              value={batchConfig.seedWordsText}
              onChange={e => setBatchConfig(prev => ({ ...prev, seedWordsText: e.target.value }))}
              className="w-full min-h-24 px-3 py-2 rounded-lg border border-gray-300"
              placeholder="Optional words to include (comma or newline separated)"
            />
            <button
              onClick={handleGenerateBatch}
              disabled={generating}
              className="bg-rose-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-rose-700 disabled:opacity-50"
            >
              {generating ? 'Generating...' : 'Generate Batch'}
            </button>
          </section>

          <section className="bg-white border border-gray-100 rounded-2xl p-4 space-y-3">
            <h2 className="font-semibold text-gray-900">➕ Add Single Word</h2>
            <form onSubmit={handleCreateWord} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  value={newWord.word || ''}
                  onChange={e => setNewWord(prev => ({ ...prev, word: e.target.value }))}
                  className="px-3 py-2 rounded-lg border border-gray-300"
                  placeholder="Word"
                  required
                />
                <input
                  value={newWord.topic || ''}
                  onChange={e => setNewWord(prev => ({ ...prev, topic: e.target.value }))}
                  className="px-3 py-2 rounded-lg border border-gray-300"
                  placeholder="Topic"
                  required
                />
                <input
                  value={newWord.partOfSpeech || ''}
                  onChange={e => setNewWord(prev => ({ ...prev, partOfSpeech: e.target.value }))}
                  className="px-3 py-2 rounded-lg border border-gray-300"
                  placeholder="Part of speech"
                />
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  Level
                  <input
                    type="number"
                    min={1}
                    max={5}
                    value={newWord.level || 2}
                    onChange={e => setNewWord(prev => ({ ...prev, level: Number(e.target.value) as 1 | 2 | 3 | 4 | 5 }))}
                    className="w-20 px-3 py-2 rounded-lg border border-gray-300"
                  />
                </label>
              </div>

              <input
                value={newWord.englishDefinition || ''}
                onChange={e => setNewWord(prev => ({ ...prev, englishDefinition: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-gray-300"
                placeholder="Simple English definition"
                required
              />
              <input
                value={newWord.dutchDescription || ''}
                onChange={e => setNewWord(prev => ({ ...prev, dutchDescription: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-gray-300"
                placeholder="Simple Dutch description"
                required
              />
              <input
                value={newWord.exampleEnglish || ''}
                onChange={e => setNewWord(prev => ({ ...prev, exampleEnglish: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-gray-300"
                placeholder="English example sentence"
              />
              <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">Save Word</button>
            </form>
          </section>

          <section className="space-y-3">
            <h2 className="font-semibold text-gray-900">📖 Word List</h2>
            {loading && <div className="text-sm text-gray-500">Loading words...</div>}
            {words.map(word => (
              <div key={word.wordId} className="bg-white border border-gray-100 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-gray-900">{word.word}</div>
                    <div className="text-xs text-gray-500">{word.topic} · level {word.level} · {word.partOfSpeech}</div>
                    <p className="text-sm text-gray-700 mt-1">{word.englishDefinition}</p>
                    <p className="text-sm text-gray-600">{word.dutchDescription}</p>
                  </div>
                  <button
                    onClick={async () => {
                      if (!confirm(`Delete "${word.word}"?`)) return;
                      const ok = await deleteWord(word.wordId);
                      if (ok) setMessage(`Deleted ${word.word}`);
                    }}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </section>
        </div>
      )}
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
        active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-800'
      }`}
    >
      {children}
    </button>
  );
}

function StatCard({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="bg-white/70 rounded-xl p-3">
      <div className={`text-xl font-bold ${color || 'text-gray-900'}`}>{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}
